import {
  autoUpdate,
  flip,
  offset,
  size as floatingSize,
  useFloating,
} from "@floating-ui/react-dom";
import { useState, useEffect, useRef, useCallback } from "react";

import type { AutoCompleteOption } from "../types";
import { orderByQuery } from "../utils";

function stopScrollPropagation(e: Event) {
  e.stopPropagation();
}

interface UseAutoCompleteParams {
  options: AutoCompleteOption[];
  value?: string | null;
  onSelect?: (value: string | null) => void;
  multi: boolean;
  addNewOption: boolean;
  onInputChange?: (value: string) => void;
  popoverMinWidth?: number;
}

export const useAutoComplete = ({
  options,
  value,
  onSelect,
  multi,
  addNewOption,
  onInputChange,
  popoverMinWidth,
}: UseAutoCompleteParams) => {
  const [query, setQuery] = useState("");
  const blurValueSetRef = useRef(false);
  const floatingNodeRef = useRef<HTMLElement | null>(null);

  const { refs, floatingStyles } = useFloating({
    placement: "bottom-start",
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      floatingSize({
        apply({ rects, elements, availableWidth, availableHeight }) {
          if (popoverMinWidth) {
            Object.assign(elements.floating.style, {
              minWidth: `${Math.max(rects.reference.width, popoverMinWidth)}px`,
              width: "max-content",
              maxWidth: `${Math.min(availableWidth, 320)}px`,
              maxHeight: `${Math.min(availableHeight, 240)}px`,
            });
            return;
          }

          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: `${Math.min(availableHeight, 240)}px`,
          });
        },
        padding: 8,
      }),
    ],
    whileElementsMounted: autoUpdate,
    strategy: "fixed",
  });

  const selectedItem =
    multi || !value ? null : options.find((item: AutoCompleteOption) => item.id === value) || null;

  useEffect(() => {
    if (multi) {
      return;
    }

    if (onInputChange) {
      return;
    }
    if (selectedItem) {
      setQuery(selectedItem.label);
      return;
    }

    setQuery(value || "");
  }, [value, selectedItem, multi, onInputChange]);

  const getFilteredItems = () => {
    if (onInputChange || query === "") {
      return options;
    }

    return orderByQuery(
      options.filter((item: AutoCompleteOption) =>
        item.label?.toLowerCase().includes(query.toLowerCase()),
      ),
      query,
    );
  };

  const filteredItems = getFilteredItems();

  const handleClear = (e: React.MouseEvent<SVGSVGElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect?.("");
    setQuery("");
    onInputChange?.("");
  };

  const handleInputBlur = () => {
    if (addNewOption && query.trim() !== "" && !multi) {
      const trimmedQuery = query.trim();
      const exactMatch = options.find((item: AutoCompleteOption) => item.label === trimmedQuery);

      blurValueSetRef.current = true;

      if (exactMatch) {
        onSelect?.(exactMatch.id);
        setQuery(exactMatch.label);
      } else {
        onSelect?.(trimmedQuery);
        setQuery(trimmedQuery);
      }

      setTimeout(() => {
        blurValueSetRef.current = false;
      }, 100);
    }
  };

  const handleComboboxChange = (item: AutoCompleteOption | null) => {
    if (blurValueSetRef.current) {
      return;
    }
    onSelect?.(item?.id || null);
  };

  const setFloatingRef = useCallback(
    (node: HTMLElement | null) => {
      if (floatingNodeRef.current) {
        floatingNodeRef.current.removeEventListener("wheel", stopScrollPropagation);
        floatingNodeRef.current.removeEventListener("touchmove", stopScrollPropagation);
      }

      refs.setFloating(node);
      floatingNodeRef.current = node;

      if (node) {
        node.addEventListener("wheel", stopScrollPropagation, { passive: false });
        node.addEventListener("touchmove", stopScrollPropagation, { passive: false });
      }
    },
    [refs],
  );

  return {
    query,
    setQuery,
    refs: {
      setReference: refs.setReference,
      setFloating: setFloatingRef,
    },
    floatingStyles,
    selectedItem,
    filteredItems,
    handleClear,
    handleInputBlur,
    handleComboboxChange,
  };
};
